import { Fragment, useEffect, useState } from "react";
import { Badge, Button, Card } from "@vb/ui";
import type {
  AdminContactItem,
  AdminStats,
  CreateOperatorResponse,
  OperatorStatus,
  OperatorSummary,
} from "@vb/rgs-contract";
import { Seo } from "../lib/seo.js";
import { Section } from "../components/Section.js";
import {
  admin,
  clearToken,
  loadToken,
  saveToken,
  type AdminNotification,
  type AdminSession,
  type OperatorKit,
  type Settlement,
} from "../lib/admin-client.js";

type Tab =
  | "overview"
  | "operators"
  | "sessions"
  | "delivery"
  | "contacts"
  | "notifications";

export function Admin() {
  const [token, setToken] = useState<string | null>(loadToken());
  const [verified, setVerified] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (!token) return;
    setVerifying(true);
    admin
      .verify()
      .then(() => {
        setVerified(true);
        setLoginError(null);
      })
      .catch((e: Error) => {
        setVerified(false);
        setLoginError(e.message);
        clearToken();
        setToken(null);
      })
      .finally(() => setVerifying(false));
  }, [token]);

  if (!token || !verified) {
    return (
      <LoginScreen
        onLogin={(t) => {
          saveToken(t);
          setToken(t);
        }}
        error={loginError}
        verifying={verifying}
      />
    );
  }

  return <Dashboard onLogout={() => { clearToken(); setToken(null); setVerified(false); }} />;
}

function LoginScreen({
  onLogin,
  error,
  verifying,
}: {
  onLogin: (token: string) => void;
  error: string | null;
  verifying: boolean;
}) {
  const [value, setValue] = useState("");
  return (
    <>
      <Seo title="Admin · login · Vault Breaker Studios" path="/admin" />
      <Section style={{ paddingTop: 120, paddingBottom: 120, maxWidth: 480 }}>
        <Badge kind="bust" style={{ marginBottom: 14 }}>
          RESTRICTED
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 40,
            margin: 0,
            textTransform: "uppercase",
          }}
        >
          Studio admin
        </h1>
        <p style={{ color: "var(--fg-2)", marginTop: 12 }}>
          Paste the admin token. You set this via the <code>ADMIN_TOKEN</code> env var on the
          game-server.
        </p>
        <Card style={{ padding: 24, marginTop: 20 }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (value.trim()) onLogin(value.trim());
            }}
            style={{ display: "grid", gap: 12 }}
          >
            <input
              className="vb-text-input"
              type="password"
              placeholder="admin token"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
            />
            {error && (
              <span style={{ color: "var(--red)", fontSize: 12, fontFamily: "var(--font-mono)" }}>
                {error}
              </span>
            )}
            <Button variant="gold" type="submit" disabled={verifying || !value.trim()}>
              {verifying ? "VERIFYING…" : "SIGN IN"}
            </Button>
          </form>
        </Card>
      </Section>
    </>
  );
}

function Dashboard({ onLogout }: { onLogout: () => void }) {
  const [tab, setTab] = useState<Tab>("overview");
  // Unread-notification badge count, owned by the Dashboard so it stays
  // visible across tab switches. The Notifications panel pushes updates up
  // via the onUnreadChange callback whenever it polls or the founder marks
  // notifications read; the dashboard also polls a lightweight count itself
  // so the badge ticks even when the founder is on another tab.
  const [unread, setUnread] = useState<number>(0);

  useEffect(() => {
    let cancelled = false;
    const poll = async (): Promise<void> => {
      try {
        const r = await admin.listNotifications({ limit: 1 });
        if (!cancelled) setUnread(r.unreadCount);
      } catch {
        // ignore — tab badge degrades gracefully
      }
    };
    poll();
    const id = setInterval(poll, 30_000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  return (
    <>
      <Seo title="Admin · Vault Breaker Studios" path="/admin" />
      <Section style={{ paddingTop: 40, paddingBottom: 20 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <span
              className="label"
              style={{
                display: "inline-block",
                padding: "3px 10px",
                borderRadius: 999,
                background: "var(--bg-3)",
                border: "1px solid var(--line-strong)",
                color: "var(--fg-3)",
                fontSize: 10,
                marginBottom: 8,
              }}
            >
              STUDIO ADMIN
            </span>
            <h1 style={{ fontFamily: "var(--font-display)", fontSize: 36, margin: 0, textTransform: "uppercase" }}>
              Control panel
            </h1>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button variant={tab === "overview" ? "gold" : "ghost"} onClick={() => setTab("overview")}>
              Overview
            </Button>
            <Button variant={tab === "operators" ? "gold" : "ghost"} onClick={() => setTab("operators")}>
              Operators
            </Button>
            <Button variant={tab === "sessions" ? "gold" : "ghost"} onClick={() => setTab("sessions")}>
              Sessions
            </Button>
            <Button variant={tab === "delivery" ? "gold" : "ghost"} onClick={() => setTab("delivery")}>
              Delivery
            </Button>
            <Button variant={tab === "contacts" ? "gold" : "ghost"} onClick={() => setTab("contacts")}>
              Contacts
            </Button>
            <Button
              variant={tab === "notifications" ? "gold" : "ghost"}
              onClick={() => setTab("notifications")}
            >
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                Notifications
                {unread > 0 && (
                  <span
                    aria-label={`${unread} unread`}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 18,
                      height: 18,
                      padding: "0 6px",
                      borderRadius: 999,
                      background: "var(--red)",
                      color: "white",
                      fontSize: 10,
                      fontWeight: 700,
                      fontFamily: "var(--font-mono)",
                      lineHeight: 1,
                    }}
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </span>
            </Button>
            <Button variant="danger" onClick={onLogout}>
              Sign out
            </Button>
          </div>
        </div>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        {tab === "overview" && <OverviewPanel />}
        {tab === "operators" && <OperatorsPanel />}
        {tab === "sessions" && <SessionsPanel />}
        {tab === "delivery" && <DeliveryPanel />}
        {tab === "contacts" && <ContactsPanel />}
        {tab === "notifications" && <NotificationsPanel onUnreadChange={setUnread} />}
      </Section>
    </>
  );
}

function OverviewPanel() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setStats(await admin.getStats());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 10_000); // auto-refresh every 10s
    return () => clearInterval(id);
  }, []);

  if (loading && !stats) return <div style={{ color: "var(--fg-3)" }}>Loading…</div>;
  if (err && !stats) return <div style={{ color: "var(--red)" }}>{err}</div>;
  if (!stats) return null;

  const fmtNum = (n: number) => n.toLocaleString();
  const fmtBtc = (n: number) => `${n.toFixed(5)} BTC`;
  const fmtDuration = (sec: number) => {
    const d = Math.floor(sec / 86400);
    const h = Math.floor((sec % 86400) / 3600);
    const m = Math.floor((sec % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        }}
      >
        <StatCard label="Operators · active" value={fmtNum(stats.operators.active)}
                  sub={`${fmtNum(stats.operators.suspended)} suspended · ${fmtNum(stats.operators.terminated)} terminated`}
                  tone="gold" />
        <StatCard label="Live sessions" value={fmtNum(stats.sessions.total)}
                  sub={`${fmtNum(stats.sessions.activeRounds)} rounds in progress`}
                  tone="cyan" />
        <StatCard label="Rounds · 30d" value={fmtNum(stats.last30d.rounds)} tone="default" />
        <StatCard label="Wagered · 30d" value={fmtBtc(stats.last30d.wagered)} tone="default" />
        <StatCard label="GGR · 30d" value={fmtBtc(stats.last30d.ggr)}
                  sub={stats.last30d.wagered > 0 ? `${((stats.last30d.ggr / stats.last30d.wagered) * 100).toFixed(2)}% house edge realized` : "—"}
                  tone={stats.last30d.ggr >= 0 ? "green" : "red"} />
        <StatCard label="Server uptime" value={fmtDuration(stats.uptimeSec)} sub={`Node ${stats.nodeVersion}`} tone="default" />
      </div>

      {/* Per-game KPI cards — three side-by-side, one per game. Empty rows
          (no rounds yet for a game) render placeholder cards so the
          three-column grid stays consistent visually. */}
      <PerGameOverview perGame={stats.perGame ?? []} />

      <Card style={{ padding: 20 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Quick links</h3>
        <p style={{ margin: "8px 0", color: "var(--fg-2)", fontSize: 13 }}>
          Provably-fair verification: <code className="mono">POST /rgs/verify</code>. Round seeds:{" "}
          <code className="mono">GET /rgs/round/:id/seeds</code>. Health:{" "}
          <code className="mono">GET /health</code>.
        </p>
      </Card>
    </div>
  );
}

/** Three-column per-game KPI grid for the Overview tab. Each card shows
 *  rounds / wagered / GGR / empirical RTP for one game over the last 30
 *  days, with a small color tab to identify the game at a glance. */
function PerGameOverview({ perGame }: { perGame: AdminStats["perGame"] }) {
  const GAMES: Array<{
    slug: "vault-breaker" | "pressure-plate" | "cipher-crack";
    name: string;
    accent: string;
  }> = [
    { slug: "vault-breaker", name: "Vault Breaker", accent: "var(--gold-bright)" },
    { slug: "pressure-plate", name: "Pressure Plate", accent: "var(--cyan)" },
    { slug: "cipher-crack", name: "Cipher Crack", accent: "var(--green)" },
  ];
  const byslug = new Map(perGame.map((p) => [p.gameSlug, p]));

  return (
    <Card style={{ padding: 20, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Per-game · 30d</h3>
        <span style={{ color: "var(--fg-3)", fontSize: 12 }}>
          rounds · wagered · GGR · empirical RTP
        </span>
      </div>
      <div
        style={{
          display: "grid",
          gap: 12,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        }}
      >
        {GAMES.map(({ slug, name, accent }) => {
          const k = byslug.get(slug);
          const rounds = k?.rounds ?? 0;
          const wagered = k?.wagered ?? 0;
          const ggr = k?.ggr ?? 0;
          const rtp = k?.rtp ?? 0;
          return (
            <div
              key={slug}
              style={{
                padding: 14,
                border: "1px solid var(--line)",
                borderTop: `3px solid ${accent}`,
                borderRadius: 8,
                background: "var(--bg-2)",
                display: "grid",
                gap: 6,
              }}
            >
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 13,
                  letterSpacing: "0.06em",
                  color: accent,
                  textTransform: "uppercase",
                }}
              >
                {name}
              </span>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                <Mini label="Rounds" value={rounds.toLocaleString()} />
                <Mini label="Wagered" value={wagered.toFixed(5)} />
                <Mini
                  label="GGR"
                  value={ggr.toFixed(5)}
                  tone={ggr >= 0 ? "green" : "red"}
                />
                <Mini
                  label="RTP"
                  value={wagered > 0 ? `${(rtp * 100).toFixed(2)}%` : "—"}
                />
              </div>
              {/* Per-currency breakdown: shown only when this game saw
                  multiple currencies in the window. Single-currency rows
                  are implicit from the wagered display. */}
              {k && Object.keys(k.byCurrency).length > 1 && (
                <div
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    color: "var(--fg-3)",
                    paddingTop: 4,
                    borderTop: "1px dashed var(--line)",
                  }}
                >
                  {Object.entries(k.byCurrency).map(([cur, v]) => (
                    <div key={cur}>
                      {cur}: wagered {v.wagered.toFixed(2)} · paid {v.paid.toFixed(2)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Mini({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "green" | "red";
}) {
  const color =
    tone === "green" ? "var(--green)" : tone === "red" ? "var(--red)" : "var(--fg-1)";
  return (
    <div style={{ display: "grid", gap: 1 }}>
      <span
        className="label"
        style={{ fontSize: 9, color: "var(--fg-3)", letterSpacing: "0.1em" }}
      >
        {label}
      </span>
      <span
        className="mono"
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          color,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  tone = "default",
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "default" | "gold" | "cyan" | "green" | "red";
}) {
  const accent = {
    default: "var(--fg-1)",
    gold: "var(--gold-bright)",
    cyan: "var(--cyan)",
    green: "var(--green)",
    red: "var(--red)",
  }[tone];
  return (
    <Card style={{ padding: 16, display: "grid", gap: 4 }}>
      <span className="label" style={{ color: "var(--fg-3)", fontSize: 10 }}>{label}</span>
      <span
        className="mono"
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 28,
          color: accent,
          letterSpacing: -0.5,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
      {sub && (
        <span style={{ color: "var(--fg-3)", fontSize: 11, fontFamily: "var(--font-mono)" }}>
          {sub}
        </span>
      )}
    </Card>
  );
}

/** GameFilter pill — appears at the top of admin tabs that benefit from
 *  game-scoped narrowing. State is local to the parent tab; URL persistence
 *  is a follow-up. "All" passes through every row. */
type GameFilterValue = "all" | "vault-breaker" | "pressure-plate" | "cipher-crack";
function GameFilter({
  value,
  onChange,
}: {
  value: GameFilterValue;
  onChange: (v: GameFilterValue) => void;
}) {
  const opts: Array<{ v: GameFilterValue; label: string; accent: string }> = [
    { v: "all", label: "All games", accent: "var(--fg-1)" },
    { v: "vault-breaker", label: "Vault Breaker", accent: "var(--gold-bright)" },
    { v: "pressure-plate", label: "Pressure Plate", accent: "var(--cyan)" },
    { v: "cipher-crack", label: "Cipher Crack", accent: "var(--green)" },
  ];
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
      {opts.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            style={{
              padding: "5px 12px",
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              border: `1px solid ${active ? o.accent : "var(--line)"}`,
              borderRadius: 999,
              background: active ? "var(--bg-3)" : "transparent",
              color: active ? o.accent : "var(--fg-2)",
              cursor: "pointer",
              fontFamily: "var(--font-ui)",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** Game-slug → short badge config used in admin table cells. Mirrors the
 *  badges in LivePlayerFeed so the visual language is consistent. */
const ADMIN_GAME_BADGES: Record<
  "vault-breaker" | "pressure-plate" | "cipher-crack",
  { label: string; bg: string; fg: string }
> = {
  "vault-breaker": { label: "VB", bg: "rgba(245,197,109,0.15)", fg: "#F5C56D" },
  "pressure-plate": { label: "PP", bg: "rgba(0,229,255,0.15)", fg: "#5DD3E8" },
  "cipher-crack": { label: "CC", bg: "rgba(120,255,140,0.13)", fg: "#7CD9A0" },
};
function GameBadge({ slug }: { slug: keyof typeof ADMIN_GAME_BADGES }) {
  const tag = ADMIN_GAME_BADGES[slug];
  return (
    <span
      style={{
        padding: "1px 6px",
        fontFamily: "var(--font-mono)",
        fontSize: 9,
        fontWeight: 700,
        letterSpacing: 0.5,
        borderRadius: 3,
        background: tag.bg,
        color: tag.fg,
      }}
      title={slug}
    >
      {tag.label}
    </span>
  );
}

function SessionsPanel() {
  const [sessions, setSessions] = useState<AdminSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [gameFilter, setGameFilter] = useState<GameFilterValue>("all");

  const refresh = async () => {
    try {
      setSessions(await admin.listSessions());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, 5_000); // every 5s
    return () => clearInterval(id);
  }, []);

  if (loading) return <div style={{ color: "var(--fg-3)" }}>Loading…</div>;
  if (err) return <div style={{ color: "var(--red)" }}>{err}</div>;

  // Apply game filter client-side. The data set is tiny (in-memory sessions
  // capped at a few hundred), so a client filter is cheaper than another
  // round-trip with a query param.
  const visible =
    gameFilter === "all" ? sessions : sessions.filter((s) => s.gameSlug === gameFilter);

  if (sessions.length === 0) {
    return (
      <>
        <GameFilter value={gameFilter} onChange={setGameFilter} />
        <Card style={{ padding: 20, color: "var(--fg-3)" }}>
          No active sessions. The game-server is running, but no players are
          connected right now.
        </Card>
      </>
    );
  }

  return (
    <>
      <GameFilter value={gameFilter} onChange={setGameFilter} />
      <Card style={{ padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--line-strong)" }}>
              <Th>Session</Th>
              <Th>Game</Th>
              <Th>Operator</Th>
              <Th>Player</Th>
              <Th>Currency</Th>
              <Th>Balance</Th>
              <Th>Nonce</Th>
              <Th>Round</Th>
              <Th>Sockets</Th>
            </tr>
          </thead>
          <tbody>
            {visible.map((s) => (
              <tr key={s.sessionId} style={{ borderTop: "1px solid var(--line)" }}>
                <Td mono>{s.sessionId.slice(0, 14)}…</Td>
                <Td>
                  <GameBadge slug={s.gameSlug} />
                </Td>
                <Td mono>{s.operatorId}</Td>
                <Td mono>{s.playerId}</Td>
                <Td>{s.currency}</Td>
                <Td mono>{s.balance.toFixed(s.currency === "BTC" ? 8 : 5)}</Td>
                <Td mono>{s.nonce}</Td>
                <Td mono>
                  <span style={{ color: s.activeRoundId ? "var(--cyan)" : "var(--fg-3)" }}>
                    {s.activeRoundId ? s.activeRoundId.slice(0, 12) + "…" : "—"}
                  </span>
                </Td>
                <Td mono>{s.socketCount}</Td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td
                  colSpan={9}
                  style={{
                    padding: 16,
                    textAlign: "center",
                    color: "var(--fg-3)",
                    fontSize: 12,
                  }}
                >
                  No sessions match the current filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Card>
    </>
  );
}

function statusColor(s: OperatorStatus): string {
  return s === "active" ? "var(--green)" : s === "suspended" ? "var(--gold-bright)" : "var(--red)";
}

function OperatorsPanel() {
  const [ops, setOps] = useState<OperatorSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{ id: string; apiKey: string } | null>(null);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const [createWebsite, setCreateWebsite] = useState("");
  const [createContact, setCreateContact] = useState("");
  const [createNotes, setCreateNotes] = useState("");
  const [expandedOpId, setExpandedOpId] = useState<string | null>(null);
  /** Operator IDs whose KIT is currently expanded inline. */
  const [openKitId, setOpenKitId] = useState<string | null>(null);
  const [openKitData, setOpenKitData] = useState<OperatorKit | null>(null);
  /** Operator currently shown in the SALES EMAIL modal. */
  const [salesEmailFor, setSalesEmailFor] = useState<OperatorSummary | null>(null);

  const showKit = async (operatorId: string): Promise<void> => {
    if (openKitId === operatorId) {
      setOpenKitId(null);
      setOpenKitData(null);
      return;
    }
    setOpenKitId(operatorId);
    setOpenKitData(null);
    try {
      const kit = await admin.getKit(operatorId);
      setOpenKitData(kit);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Kit load failed");
      setOpenKitId(null);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      setOps(await admin.listOperators());
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const changeStatus = async (id: string, status: OperatorStatus) => {
    const reason =
      status === "active"
        ? undefined
        : prompt(`Reason for marking ${id} as ${status}?`) ?? undefined;
    if (status !== "active" && !reason) return; // user cancelled
    try {
      await admin.setStatus(id, { status, reason });
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  const rotate = async (id: string) => {
    if (!confirm(`Rotate API key for ${id}? The old key stops working immediately.`)) return;
    try {
      const r = await admin.rotateKey(id);
      setNewKey({ id, apiKey: r.apiKey });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  const deleteOp = async (id: string) => {
    const typed = prompt(`Type ${id} to confirm deletion`);
    if (typed !== id) return;
    try {
      await admin.deleteOperator(id);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  const toggleTransparency = async (id: string, current: boolean) => {
    if (!current) {
      const ok = confirm(
        `Publish a public transparency page for ${id}?\n\n` +
          `It will display 30-day actual RTP, total wagered/paid, and biggest cashouts. ` +
          `Operator must consent to having these stats publicly viewable.`,
      );
      if (!ok) return;
    }
    try {
      await admin.setTransparency(id, !current);
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  const createOp = async () => {
    if (!createName.trim()) return;
    setCreating(true);
    try {
      const r: CreateOperatorResponse = await admin.createOperator({
        name: createName.trim(),
        billingEmail: createEmail.trim() || undefined,
        websiteUrl: createWebsite.trim() || undefined,
        contactName: createContact.trim() || undefined,
        notes: createNotes.trim() || undefined,
      });
      setNewKey({ id: r.operator.id, apiKey: r.apiKey });
      setCreateName("");
      setCreateEmail("");
      setCreateWebsite("");
      setCreateContact("");
      setCreateNotes("");
      await refresh();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <Card style={{ padding: 20, display: "grid", gap: 12 }}>
        <h3 style={{ margin: 0 }}>Add new casino</h3>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            className="vb-text-input"
            placeholder="Casino name (e.g. Acme Casino)"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <input
            className="vb-text-input"
            placeholder="Billing email (optional)"
            value={createEmail}
            onChange={(e) => setCreateEmail(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            className="vb-text-input"
            placeholder="Website URL (e.g. https://acmecasino.com)"
            value={createWebsite}
            onChange={(e) => setCreateWebsite(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
          <input
            className="vb-text-input"
            placeholder="Contact name (BD lead, optional)"
            value={createContact}
            onChange={(e) => setCreateContact(e.target.value)}
            style={{ flex: 1, minWidth: 220 }}
          />
        </div>
        <textarea
          className="vb-text-input"
          placeholder="Notes (where you found them, expected go-live, anything useful…)"
          value={createNotes}
          onChange={(e) => setCreateNotes(e.target.value)}
          rows={3}
          style={{ width: "100%", resize: "vertical", fontFamily: "inherit" }}
        />
        <div>
          <Button variant="gold" onClick={createOp} disabled={creating || !createName.trim()}>
            {creating ? "ADDING…" : "ADD CASINO"}
          </Button>
        </div>
      </Card>

      {newKey && (
        <OnboardingKit
          operatorId={newKey.id}
          apiKey={newKey.apiKey}
          onDismiss={() => setNewKey(null)}
        />
      )}

      {salesEmailFor && (
        <SalesEmailModal operator={salesEmailFor} onClose={() => setSalesEmailFor(null)} />
      )}

      {loading ? (
        <div style={{ color: "var(--fg-3)" }}>Loading…</div>
      ) : err ? (
        <div style={{ color: "var(--red)" }}>{err}</div>
      ) : (
        <Card style={{ padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 820 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--line-strong)" }}>
                <Th>ID</Th>
                <Th>Name</Th>
                <Th>Status</Th>
                <Th>Rounds 30d</Th>
                <Th>GGR 30d</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody>
              {ops.map((op) => {
                const meta = op.meta;
                const hasCaption = !!(meta?.websiteUrl || meta?.contactName);
                const hasNotes = !!meta?.notes?.trim();
                const isExpanded = expandedOpId === op.id;
                const websiteHost = meta?.websiteUrl
                  ? meta.websiteUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")
                  : null;
                return (
                <Fragment key={op.id}>
                <tr style={{ borderTop: "1px solid var(--line)" }}>
                  <Td mono>{op.id}</Td>
                  <Td>
                    <div
                      onClick={() => hasNotes && setExpandedOpId(isExpanded ? null : op.id)}
                      style={{ cursor: hasNotes ? "pointer" : "default" }}
                      title={hasNotes ? "Click to expand notes" : undefined}
                    >
                      <div>{op.name}</div>
                      {hasCaption && (
                        <div style={{ fontSize: 10, color: "var(--fg-3)", marginTop: 2 }}>
                          {websiteHost && (
                            <a
                              href={meta!.websiteUrl}
                              target="_blank"
                              rel="noreferrer noopener"
                              onClick={(e) => e.stopPropagation()}
                              style={{ color: "var(--cyan)", textDecoration: "none" }}
                            >
                              {websiteHost}
                            </a>
                          )}
                          {websiteHost && meta?.contactName && (
                            <span style={{ color: "var(--fg-3)" }}> · </span>
                          )}
                          {meta?.contactName && <>Contact: {meta.contactName}</>}
                          {hasNotes && (
                            <span style={{ marginLeft: 6, color: "var(--cyan)" }}>
                              {isExpanded ? "▾" : "▸"} notes
                            </span>
                          )}
                        </div>
                      )}
                      {!hasCaption && hasNotes && (
                        <div style={{ fontSize: 10, color: "var(--cyan)", marginTop: 2 }}>
                          {isExpanded ? "▾" : "▸"} notes
                        </div>
                      )}
                    </div>
                  </Td>
                  <Td>
                    <span
                      style={{
                        color: statusColor(op.status),
                        fontWeight: 700,
                        textTransform: "uppercase",
                        fontSize: 11,
                        letterSpacing: "0.1em",
                      }}
                    >
                      {op.status}
                    </span>
                    {op.suspendReason && (
                      <div style={{ fontSize: 11, color: "var(--fg-3)" }}>{op.suspendReason}</div>
                    )}
                  </Td>
                  <Td mono>{op.rounds30d ?? 0}</Td>
                  <Td mono>{(op.ggr30d ?? 0).toFixed(5)}</Td>
                  <Td>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {op.status !== "active" && (
                        <MiniButton onClick={() => changeStatus(op.id, "active")} tone="green">
                          Resume
                        </MiniButton>
                      )}
                      {op.status === "active" && (
                        <MiniButton onClick={() => changeStatus(op.id, "suspended")} tone="gold">
                          Suspend
                        </MiniButton>
                      )}
                      {op.status !== "terminated" && (
                        <MiniButton onClick={() => changeStatus(op.id, "terminated")} tone="red">
                          Terminate
                        </MiniButton>
                      )}
                      <MiniButton onClick={() => rotate(op.id)} tone="cyan">
                        Rotate key
                      </MiniButton>
                      <MiniButton onClick={() => showKit(op.id)} tone="cyan">
                        {openKitId === op.id ? "Hide kit" : "Kit"}
                      </MiniButton>
                      <MiniButton onClick={() => setSalesEmailFor(op)} tone="gold">
                        Sales email
                      </MiniButton>
                      <MiniButton
                        onClick={() => toggleTransparency(op.id, !!op.transparencyOptin)}
                        tone={op.transparencyOptin ? "green" : "cyan"}
                      >
                        {op.transparencyOptin ? "Public ✓" : "Make public"}
                      </MiniButton>
                      {op.id !== "demo" && (
                        <MiniButton onClick={() => deleteOp(op.id)} tone="red">
                          Delete
                        </MiniButton>
                      )}
                    </div>
                  </Td>
                </tr>
                {openKitId === op.id && (
                  <tr style={{ background: "var(--bg-1)" }}>
                    <td colSpan={6} style={{ padding: "12px 14px", borderTop: "1px solid var(--line)" }}>
                      {!openKitData ? (
                        <div style={{ fontSize: 12, color: "var(--fg-3)" }}>Loading kit…</div>
                      ) : (
                        <KitInline kit={openKitData} />
                      )}
                    </td>
                  </tr>
                )}
                {isExpanded && hasNotes && (
                  <tr style={{ background: "var(--bg-1)" }}>
                    <td colSpan={6} style={{ padding: "10px 14px", borderTop: "1px solid var(--line)" }}>
                      <div
                        style={{
                          fontSize: 12,
                          color: "var(--fg-2)",
                          fontFamily: "var(--font-mono)",
                          whiteSpace: "pre-wrap",
                          lineHeight: 1.5,
                        }}
                      >
                        {meta!.notes}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function ContactsPanel() {
  const [contacts, setContacts] = useState<AdminContactItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [newKey, setNewKey] = useState<{ id: string; apiKey: string } | null>(null);

  useEffect(() => {
    admin
      .listContacts()
      .then(setContacts)
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, []);

  const approveAsOperator = async (c: AdminContactItem) => {
    const payload = (c.payload ?? {}) as { operator?: string; email?: string };
    const name = payload.operator ?? "Unnamed operator";
    try {
      const r = await admin.createOperator({
        name,
        billingEmail: payload.email,
        fromContactId: c.id,
      });
      setNewKey({ id: r.operator.id, apiKey: r.apiKey });
      const refreshed = await admin.listContacts();
      setContacts(refreshed);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed");
    }
  };

  if (loading) return <div style={{ color: "var(--fg-3)" }}>Loading…</div>;
  if (err) return <div style={{ color: "var(--red)" }}>{err}</div>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      {newKey && (
        <OnboardingKit
          operatorId={newKey.id}
          apiKey={newKey.apiKey}
          onDismiss={() => setNewKey(null)}
        />
      )}

      {contacts.length === 0 && <div style={{ color: "var(--fg-3)" }}>No contact submissions yet.</div>}

      {contacts.map((c) => {
        const payload = (c.payload ?? {}) as Record<string, unknown>;
        return (
          <Card key={c.id} style={{ padding: 18, display: "grid", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: 0 }}>{String(payload.operator ?? "—")}</h3>
                <div style={{ fontSize: 12, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
                  {new Date(c.ts).toISOString()} · {String(payload.country ?? "—")} · tier{" "}
                  {String(payload.volumeTier ?? "—")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <span
                  style={{
                    alignSelf: "center",
                    fontSize: 11,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color:
                      c.status === "approved"
                        ? "var(--green)"
                        : c.status === "rejected"
                        ? "var(--red)"
                        : "var(--cyan)",
                  }}
                >
                  {c.status}
                </span>
                {c.status === "new" && (
                  <Button variant="gold" onClick={() => approveAsOperator(c)}>
                    Approve → operator
                  </Button>
                )}
              </div>
            </div>
            <div style={{ fontSize: 12, color: "var(--fg-2)", fontFamily: "var(--font-mono)" }}>
              {String(payload.name ?? "")} · {String(payload.email ?? "")}
            </div>
            <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)" }}>
              {String(payload.message ?? "")}
            </p>
            {c.operatorId && (
              <div style={{ fontSize: 12, color: "var(--fg-3)" }}>
                Operator: <code>{c.operatorId}</code>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// =============================================================================
// NotificationsPanel — in-app notifications stream (replaces the old
// Slack/email pings). Polls every 30s + on tab focus, surfaces a colored
// chip per kind (contact/booking/operator/round.error), and lets the founder
// dismiss individually or in bulk.
// =============================================================================
type NotificationKindStyle = {
  label: string;
  bg: string;
  fg: string;
  border: string;
};
const NOTIFICATION_KIND_STYLES: Record<string, NotificationKindStyle> = {
  "contact.submitted": {
    label: "CONTACT",
    bg: "rgba(0,229,255,0.12)",
    fg: "var(--cyan)",
    border: "var(--cyan)",
  },
  "booking.requested": {
    label: "BOOKING",
    bg: "rgba(212,162,76,0.14)",
    fg: "var(--gold-bright)",
    border: "var(--gold-bright)",
  },
  "operator.created": {
    label: "OPERATOR",
    bg: "rgba(0,217,126,0.13)",
    fg: "var(--green)",
    border: "var(--green)",
  },
  "round.error": {
    label: "ROUND ERR",
    bg: "rgba(255,59,71,0.14)",
    fg: "var(--red)",
    border: "var(--red)",
  },
};
const DEFAULT_KIND_STYLE: NotificationKindStyle = {
  label: "EVENT",
  bg: "var(--bg-3)",
  fg: "var(--fg-2)",
  border: "var(--line-strong)",
};

/** Compact relative timestamp ("just now", "5m ago", "3h ago", "Apr 21"). */
function formatRelative(epochMs: number): string {
  const diff = Date.now() - epochMs;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(epochMs).toISOString().slice(5, 10);
}

function NotificationsPanel({
  onUnreadChange,
}: {
  onUnreadChange: (count: number) => void;
}) {
  const [items, setItems] = useState<AdminNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = async (): Promise<void> => {
    try {
      const r = await admin.listNotifications({ limit: 100 });
      setItems(r.items);
      setUnreadCount(r.unreadCount);
      onUnreadChange(r.unreadCount);
      setErr(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // 30s poll cadence (low enough for live monitoring, high enough that
    // an idle admin tab doesn't hammer the server).
    const id = setInterval(refresh, 30_000);
    // Refresh immediately when the founder switches back to this tab.
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markOne = async (id: number): Promise<void> => {
    setBusy(true);
    try {
      await admin.markNotificationsRead([id]);
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Mark failed");
    } finally {
      setBusy(false);
    }
  };

  const markAll = async (): Promise<void> => {
    if (unreadCount === 0) return;
    setBusy(true);
    try {
      await admin.markAllNotificationsRead();
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Mark all failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ color: "var(--fg-3)" }}>Loading…</div>;
  if (err && items.length === 0) return <div style={{ color: "var(--red)" }}>{err}</div>;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: 18,
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            Notifications
          </h2>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--fg-3)" }}>
            {unreadCount > 0
              ? `${unreadCount} unread · refreshes every 30s`
              : "All caught up · refreshes every 30s"}
          </p>
        </div>
        <Button
          variant={unreadCount > 0 ? "gold" : "ghost"}
          onClick={markAll}
          disabled={busy || unreadCount === 0}
        >
          {busy ? "…" : "Mark all read"}
        </Button>
      </div>

      {err && items.length > 0 && (
        <div style={{ color: "var(--red)", fontSize: 12 }}>{err}</div>
      )}

      {items.length === 0 ? (
        <Card style={{ padding: 20, color: "var(--fg-3)" }}>
          No notifications yet. Contact submissions, booking requests, operator
          creations, and round-error alerts will appear here.
        </Card>
      ) : (
        <div style={{ display: "grid", gap: 10 }}>
          {items.map((n) => (
            <NotificationCard
              key={n.id}
              notification={n}
              onMarkRead={() => markOne(n.id)}
              busy={busy}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function NotificationCard({
  notification,
  onMarkRead,
  busy,
}: {
  notification: AdminNotification;
  onMarkRead: () => void;
  busy: boolean;
}) {
  const style = NOTIFICATION_KIND_STYLES[notification.kind] ?? DEFAULT_KIND_STYLE;
  const isUnread = notification.readAt == null;
  return (
    <Card
      style={{
        padding: 16,
        display: "grid",
        gap: 10,
        // Faint accent stripe on the left + softer card background once read,
        // so the eye can scan unread vs handled at a glance.
        borderLeft: `3px solid ${isUnread ? style.border : "var(--line)"}`,
        opacity: isUnread ? 1 : 0.66,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <span
            style={{
              padding: "3px 8px",
              borderRadius: 4,
              background: style.bg,
              color: style.fg,
              fontFamily: "var(--font-mono)",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.6,
            }}
            title={notification.kind}
          >
            {style.label}
          </span>
          <span style={{ color: "var(--fg-3)", fontSize: 11, fontFamily: "var(--font-mono)" }}>
            {formatRelative(notification.createdAt)}
          </span>
          <span style={{ color: "var(--fg-4)", fontSize: 10, fontFamily: "var(--font-mono)" }}>
            #{notification.id}
          </span>
        </div>
        {isUnread && (
          <button
            type="button"
            onClick={onMarkRead}
            disabled={busy}
            style={{
              padding: "3px 10px",
              fontSize: 11,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              background: "rgba(212,162,76,0.12)",
              border: "1px solid var(--gold-bright)",
              color: "var(--gold-bright)",
              borderRadius: 999,
              cursor: busy ? "not-allowed" : "pointer",
              opacity: busy ? 0.5 : 1,
              fontFamily: "var(--font-ui)",
            }}
          >
            Mark read
          </button>
        )}
      </div>
      <NotificationPayloadView kind={notification.kind} payload={notification.payload} />
    </Card>
  );
}

/** Per-kind payload renderer. Falls back to pretty-printed JSON for any kind
 *  we don't know how to format — keeps the panel useful for ad-hoc kinds
 *  emitted by future subsystems without requiring a UI deploy. */
function NotificationPayloadView({
  kind,
  payload,
}: {
  kind: string;
  payload: Record<string, unknown>;
}) {
  if (kind === "contact.submitted") {
    const operator = String(payload.operator ?? payload.operatorName ?? "—");
    const name = String(payload.name ?? "");
    const email = String(payload.email ?? "");
    const country = String(payload.country ?? "—");
    const tier = String(payload.volumeTier ?? payload.tier ?? "—");
    const message = String(payload.message ?? "");
    return (
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 14, color: "var(--fg-1)", fontWeight: 600 }}>{operator}</div>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--fg-2)",
          }}
        >
          {name}
          {name && email ? " · " : ""}
          {email}
          {(name || email) && country !== "—" ? " · " : ""}
          {country !== "—" && <>country: {country}</>}
          {tier !== "—" ? ` · tier ${tier}` : ""}
        </div>
        {message && (
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "var(--fg-2)",
              lineHeight: 1.5,
              // Excerpt only — full message lives on the Contacts tab.
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {message}
          </p>
        )}
      </div>
    );
  }

  if (kind === "booking.requested") {
    const preferredTime = String(
      payload.preferredTime ?? payload.preferred_time ?? payload.time ?? "—",
    );
    const topic = String(payload.topic ?? payload.subject ?? "—");
    const requesterName = String(payload.name ?? payload.requester ?? "");
    const email = String(payload.email ?? "");
    return (
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 14, color: "var(--fg-1)", fontWeight: 600 }}>
          {topic !== "—" ? topic : "Booking request"}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--fg-2)" }}>
          When: {preferredTime}
          {(requesterName || email) && " · "}
          {requesterName}
          {requesterName && email ? " · " : ""}
          {email}
        </div>
      </div>
    );
  }

  if (kind === "operator.created") {
    const operatorId = String(payload.operatorId ?? payload.id ?? "—");
    const operatorName = String(payload.operatorName ?? payload.name ?? "—");
    return (
      <div style={{ display: "grid", gap: 4 }}>
        <div style={{ fontSize: 14, color: "var(--fg-1)", fontWeight: 600 }}>{operatorName}</div>
        <code style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
          {operatorId}
        </code>
      </div>
    );
  }

  if (kind === "round.error") {
    const operatorId = String(payload.operatorId ?? "—");
    const roundId = String(payload.roundId ?? "—");
    const error = String(payload.error ?? payload.message ?? "unknown error");
    return (
      <div style={{ display: "grid", gap: 4 }}>
        <div style={{ fontSize: 13, color: "var(--red)", fontFamily: "var(--font-mono)" }}>
          {error}
        </div>
        <div style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "var(--font-mono)" }}>
          op: {operatorId} · round: {roundId}
        </div>
      </div>
    );
  }

  // Generic fallback — pretty-print the JSON so the panel still surfaces
  // anything new without a UI redeploy.
  return (
    <pre
      style={{
        margin: 0,
        padding: "8px 10px",
        background: "var(--bg-1)",
        border: "1px solid var(--line)",
        borderRadius: 4,
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        color: "var(--fg-2)",
        whiteSpace: "pre-wrap",
        overflow: "auto",
        maxHeight: 200,
        lineHeight: 1.5,
      }}
    >
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
}

// =============================================================================
// DeliveryPanel — "How games are delivered to operators" dashboard.
// Shows the 4-step handoff diagram, per-operator settlement state (pending
// USDT due to operators with GGR), and a recent-rounds aggregate.
// =============================================================================
function DeliveryPanel() {
  const [operators, setOperators] = useState<OperatorSummary[]>([]);
  const [settlements, setSettlements] = useState<Settlement[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([admin.listOperators(), admin.listSettlements()])
      .then(([ops, s]) => {
        if (!mounted) return;
        setOperators(ops);
        setSettlements(s.settlements);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setErr(e instanceof Error ? e.message : "Load failed");
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return <div style={{ padding: 24, color: "var(--fg-3)" }}>Loading…</div>;
  if (err) return <div style={{ padding: 24, color: "var(--red)" }}>{err}</div>;

  const liveOperators = operators.filter((o) => o.status === "active" && o.id !== "demo");
  const totalGgr30d = liveOperators.reduce((s, o) => s + (o.ggr30d ?? 0), 0);
  const studioCut30d = totalGgr30d * 0.10;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* How delivery works */}
      <Card style={{ padding: 24 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontFamily: "var(--font-display)", letterSpacing: 0.5, textTransform: "uppercase" }}>
          How delivery works
        </h2>
        <p style={{ marginTop: 8, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
          Operators don't receive a zip file. They receive an <strong>operator ID + API key + embed URL</strong> from
          this panel. Their lobby iframes the play URL with the operator ID; round outcomes settle via REST.
        </p>
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          }}
        >
          {[
            { n: "01", t: "Create operator", b: "Operators tab → Add new casino. Generates ID + API key (shown once)." },
            { n: "02", t: "Send onboarding kit", b: "Click KIT in the Operators table. Paste the embed snippet into your sales email." },
            { n: "03", t: "Operator iframes", b: "<iframe src='https://vaultbreaker.dev/play?operator=<id>' /> goes live in their lobby." },
            { n: "04", t: "Round resolves", b: "REST resolves the round + (optionally) POSTs a webhook to operator's URL." },
          ].map((step) => (
            <div
              key={step.n}
              style={{
                padding: 14,
                background: "var(--bg-3)",
                border: "1px solid var(--line)",
                borderRadius: 6,
                display: "flex",
                flexDirection: "column",
                gap: 6,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-display)",
                  fontSize: 28,
                  color: "var(--gold-bright)",
                  letterSpacing: 1,
                  lineHeight: 1,
                }}
              >
                {step.n}
              </div>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, fontWeight: 700, color: "var(--fg-1)" }}>
                {step.t}
              </div>
              <div style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--fg-2)", lineHeight: 1.45 }}>
                {step.b}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Settlement summary card */}
      <Card style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontFamily: "var(--font-display)", letterSpacing: 0.5, textTransform: "uppercase" }}>
            Settlements
          </h2>
          <div
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-3)",
              letterSpacing: 0.5,
            }}
          >
            30d studio cut · <span style={{ color: "var(--gold-bright)", fontWeight: 700 }}>{studioCut30d.toFixed(8)} BTC</span>
          </div>
        </div>
        <p style={{ marginTop: 6, fontSize: 12, color: "var(--fg-3)", lineHeight: 1.55 }}>
          You owe operators their share of GGR (90% of what they earned for you) on a weekly cadence. Mark each
          payment with the on-chain tx hash so you have an audit trail.
        </p>

        {liveOperators.length === 0 ? (
          <div
            style={{
              marginTop: 16,
              padding: 16,
              border: "1px dashed var(--line)",
              borderRadius: 4,
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              color: "var(--fg-4)",
              textAlign: "center",
            }}
          >
            No active operators yet. Once you onboard one, settlement state will appear here.
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 16 }}>
            <thead>
              <tr>
                <Th>Operator</Th>
                <Th>30d GGR</Th>
                <Th>Owed (90%)</Th>
                <Th>Last paid</Th>
                <Th>Action</Th>
              </tr>
            </thead>
            <tbody>
              {liveOperators.map((op) => {
                const owed = (op.ggr30d ?? 0) * 0.90;
                const lastSettlement = settlements.find((s) => s.operator_id === op.id);
                const lastPaidStr = lastSettlement
                  ? new Date(lastSettlement.paid_at).toISOString().slice(0, 10)
                  : "never";
                return (
                  <tr key={op.id} style={{ borderTop: "1px solid var(--line)" }}>
                    <Td>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        <span style={{ color: "var(--fg-1)" }}>{op.name}</span>
                        <span className="mono" style={{ fontSize: 10, color: "var(--fg-4)" }}>{op.id}</span>
                      </div>
                    </Td>
                    <Td mono>{(op.ggr30d ?? 0).toFixed(8)} BTC</Td>
                    <Td mono>{owed.toFixed(8)} BTC</Td>
                    <Td mono>{lastPaidStr}</Td>
                    <Td>
                      <MarkPaidButton
                        operatorId={op.id}
                        amountBtc={owed}
                        onDone={(s) => setSettlements((prev) => [s, ...prev])}
                      />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* Recent settlements log */}
      {settlements.length > 0 && (
        <Card style={{ padding: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontFamily: "var(--font-display)", letterSpacing: 0.5, textTransform: "uppercase" }}>
            Settlement log
          </h2>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
            <thead>
              <tr>
                <Th>Operator</Th>
                <Th>Amount</Th>
                <Th>Paid at</Th>
                <Th>Tx hash</Th>
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {settlements.slice(0, 20).map((s) => (
                <tr key={s.id} style={{ borderTop: "1px solid var(--line)" }}>
                  <Td mono>{s.operator_id}</Td>
                  <Td mono>{s.amount_btc.toFixed(8)} BTC</Td>
                  <Td mono>{new Date(s.paid_at).toISOString().slice(0, 16).replace("T", " ")}</Td>
                  <Td mono>
                    {s.tx_hash ? (
                      <span title={s.tx_hash}>
                        {s.tx_hash.slice(0, 8)}…{s.tx_hash.slice(-6)}
                      </span>
                    ) : (
                      <span style={{ color: "var(--fg-4)" }}>—</span>
                    )}
                  </Td>
                  <Td>{s.note ?? <span style={{ color: "var(--fg-4)" }}>—</span>}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

function MarkPaidButton({
  operatorId,
  amountBtc,
  onDone,
}: {
  operatorId: string;
  /** Owed amount in the operator's primary settlement currency. The owed
   *  number coming in is still labeled `amountBtc` for legacy reasons —
   *  in the new world it represents whatever currency the GGR was in. */
  amountBtc: number;
  onDone: (settlement: Settlement) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (amountBtc <= 0) {
      setErr("amount must be positive");
      return;
    }
    // Two-step prompt: currency first (most operators settle in USDT, but
    // BTC is still common); tx hash second. Cancel at any step bails.
    const currency = window.prompt(
      `Mark ${amountBtc.toFixed(8)} paid to ${operatorId}.\n\nWhich currency did you settle in? (BTC, ETH, SOL, USDT, USD)`,
      "USDT",
    );
    if (currency === null) return;
    const cur = currency.trim().toUpperCase();
    if (!["BTC", "ETH", "SOL", "USDT", "USD"].includes(cur)) {
      setErr(`Unknown currency: ${cur}`);
      return;
    }
    const txHash = window.prompt(
      `Tx hash (optional — paste for the audit trail, or leave blank).`,
      "",
    );
    if (txHash === null) return;
    setSubmitting(true);
    setErr(null);
    try {
      const result = await admin.recordSettlement(operatorId, {
        amount: amountBtc,
        currency: cur,
        txHash: txHash.trim() || undefined,
      });
      onDone(result.settlement);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" }}>
      <MiniButton tone="green" onClick={submit} disabled={submitting || amountBtc <= 0}>
        {submitting ? "…" : "MARK PAID"}
      </MiniButton>
      {err && <span style={{ fontSize: 10, color: "var(--red)" }}>{err}</span>}
    </div>
  );
}

// =============================================================================
// KitInline — re-pulled onboarding kit, shown when KIT button is toggled
// open in the operators table. Same shape as OnboardingKit but no API key
// (we only have the hash; rotate-key is the recovery path).
// =============================================================================
function KitInline({ kit }: { kit: OperatorKit }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 11,
          fontWeight: 800,
          letterSpacing: 1.5,
          color: "var(--gold-bright)",
          textTransform: "uppercase",
        }}
      >
        Onboarding kit · {kit.operatorName}
      </div>
      <CopyableField label="Operator ID" value={kit.operatorId} />
      <CopyableField label="Embed URL" value={kit.embedUrl} />
      <CopyableField label="API base" value={kit.apiBaseUrl} />
      <CopyableField
        label="REST endpoints"
        value={Object.entries(kit.endpoints)
          .map(([k, v]) => `${k.padEnd(14)} ${v}`)
          .join("\n")}
        multiline
      />
      <div
        style={{
          padding: "8px 12px",
          background: "rgba(11,14,22,0.5)",
          border: "1px dashed var(--line)",
          borderRadius: 4,
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--fg-3)",
          letterSpacing: 0.4,
        }}
      >
        API key: masked — only shown on create or rotate. Click ROTATE KEY in the row above to mint a new one.
      </div>
      <CopyableField
        label="Iframe snippet"
        value={`<iframe\n  src="${kit.embedUrl}"\n  width="100%"\n  height="720"\n  allow="autoplay; clipboard-read"\n  style="border:0"\n></iframe>`}
        multiline
      />
    </div>
  );
}

/** Self-contained copyable field — local copy state, reuses KitField look. */
function CopyableField({ label, value, multiline }: { label: string; value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <KitField
      label={label}
      value={value}
      copied={copied}
      onCopy={onCopy}
      multiline={multiline}
    />
  );
}

// =============================================================================
// SalesEmailModal — pre-fills the cold email template with the operator's
// name + embed URL. Single COPY action.
// =============================================================================
function SalesEmailModal({
  operator,
  onClose,
}: {
  operator: OperatorSummary;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const subject = `Cipher Crack — would this fit ${operator.name}'s lobby?`;
  const embed = `https://vaultbreaker.dev/play?operator=${operator.id}`;
  const body = `Hi ${operator.billingEmail ? operator.billingEmail.split("@")[0] : "team"},

I'm Federico, sole dev at Vault Breaker Studios. I just shipped 3 provably-fair crypto casino originals and I think one of them — Cipher Crack — would be a strong fit for ${operator.name}'s lobby.

→ Live demos:    https://vaultbreaker.dev/casino
→ Cipher Crack:  https://vaultbreaker.dev/play/cipher-crack
→ Your embed:    ${embed}

Why it's worth 5 minutes:
- Mastermind format, 30-second rounds, 20× max payout — no other operator has shipped this
- Provably-fair: server seed committed pre-round, revealed on resolve, re-derivable from (seed, client, nonce)
- 99% RTP, math sheet published on /games/cipher-crack
- 10% GGR, settled weekly in USDT/USDC — no fiat rails

If ${operator.name} runs a custom lobby and you're open to a slot for an unusual game, I can have it integrated in under a week. Single REST surface, one webhook on resolve.

Worth a 30-min call? https://vaultbreaker.dev/contact

Either way, would love your feedback on the demos.

— Federico
Vault Breaker Studios`;

  const fullEmail = `Subject: ${subject}\n\n${body}`;

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(fullEmail);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore — user can still select-all from the textarea
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 200,
        background: "rgba(6,8,15,0.72)",
        backdropFilter: "blur(8px)",
        display: "grid",
        placeItems: "center",
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-1)",
          border: "1px solid var(--line)",
          borderRadius: 6,
          maxWidth: 720,
          width: "100%",
          padding: 24,
          color: "var(--fg-1)",
          fontFamily: "var(--font-ui)",
          maxHeight: "80vh",
          overflow: "auto",
        }}
      >
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 18, letterSpacing: 1, textTransform: "uppercase" }}>
            Sales email · {operator.name}
          </h3>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "transparent",
              border: "1px solid var(--line)",
              color: "var(--fg-2)",
              padding: "6px 12px",
              borderRadius: 3,
              cursor: "pointer",
              fontSize: 11,
              fontFamily: "var(--font-ui)",
              fontWeight: 700,
              letterSpacing: 1,
            }}
          >
            CLOSE
          </button>
        </div>
        <p style={{ marginTop: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
          Pre-filled with this operator's name and embed URL. Send to{" "}
          <span className="mono" style={{ color: "var(--gold-bright)" }}>
            {operator.billingEmail ?? "their billing email"}
          </span>{" "}
          or any BD contact.
        </p>
        <textarea
          readOnly
          value={fullEmail}
          style={{
            width: "100%",
            height: 360,
            background: "var(--bg-2)",
            border: "1px solid var(--line)",
            borderRadius: 4,
            color: "var(--fg-1)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            padding: 12,
            resize: "vertical",
            lineHeight: 1.5,
          }}
        />
        <div style={{ marginTop: 14, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Button variant="gold" onClick={copy}>
            {copied ? "✓ COPIED" : "COPY EMAIL"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// OnboardingKit — shown once when an operator is created or has its key
// rotated. Bundles the API key with copy-paste-ready integration snippets so
// a new casino can wire up Vault Breaker in minutes.
// =============================================================================
function OnboardingKit({
  operatorId,
  apiKey,
  onDismiss,
}: {
  operatorId: string;
  apiKey: string;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState<string | null>(null);
  const apiBase =
    typeof window !== "undefined" && window.location.origin.includes("localhost")
      ? "http://localhost:4000"
      : "https://vaultbreaker-api.fly.dev";
  const playUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}/play?operator=${encodeURIComponent(operatorId)}`
      : `/play?operator=${encodeURIComponent(operatorId)}`;

  const copy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      setTimeout(() => setCopied((c) => (c === label ? null : c)), 1500);
    } catch {
      // ignore
    }
  };

  const envSnippet = [
    `VB_OPERATOR_ID=${operatorId}`,
    `VB_API_KEY=${apiKey}`,
    `VB_API_BASE=${apiBase}`,
  ].join("\n");

  const curlSnippet = `curl -X POST ${apiBase}/rgs/session/launch \\
  -H "content-type: application/json" \\
  -H "x-vb-operator: ${operatorId}" \\
  -H "x-vb-api-key: ${apiKey}" \\
  -d '{"operatorId":"${operatorId}","playerId":"player_001","currency":"BTC","locale":"en"}'`;

  const iframeSnippet = `<iframe
  src="${playUrl}"
  allow="autoplay; clipboard-read"
  style="width:100%;aspect-ratio:16/10;border:0"
></iframe>`;

  return (
    <Card
      style={{
        padding: 20,
        border: "1px solid var(--gold-bright)",
        background: "rgba(212,162,76,0.08)",
        display: "grid",
        gap: 16,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div>
          <Badge kind="win" style={{ marginBottom: 8 }}>
            ONBOARDING KIT
          </Badge>
          <h3 style={{ margin: 0 }}>Operator: <code className="mono">{operatorId}</code></h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--fg-2)" }}>
            Shown once. Copy these now — the API key is not retrievable later.
            Rotating replaces it immediately.
          </p>
        </div>
        <Button onClick={onDismiss}>Dismiss</Button>
      </div>

      {/* API key — the secret bit, single line, copy button */}
      <KitField
        label="API key (secret)"
        value={apiKey}
        copied={copied === "key"}
        onCopy={() => copy("key", apiKey)}
        secret
      />

      {/* Operator ID — public */}
      <KitField
        label="Operator ID"
        value={operatorId}
        copied={copied === "id"}
        onCopy={() => copy("id", operatorId)}
      />

      {/* Env file snippet */}
      <KitField
        label=".env (server-side integration)"
        value={envSnippet}
        copied={copied === "env"}
        onCopy={() => copy("env", envSnippet)}
        multiline
      />

      {/* curl test */}
      <KitField
        label="Test the API (curl)"
        value={curlSnippet}
        copied={copied === "curl"}
        onCopy={() => copy("curl", curlSnippet)}
        multiline
      />

      {/* iframe embed */}
      <KitField
        label="Iframe embed (drop into your casino lobby)"
        value={iframeSnippet}
        copied={copied === "iframe"}
        onCopy={() => copy("iframe", iframeSnippet)}
        multiline
      />

      <div style={{ fontSize: 12, color: "var(--fg-2)", lineHeight: 1.5 }}>
        <strong>Next steps</strong> — store the API key in your secret manager,
        plumb the env vars into your backend, and serve the iframe from a route
        you control. The game speaks to the RGS via session JWTs minted by{" "}
        <code className="mono">/rgs/session/launch</code>; full contract at{" "}
        <code className="mono">{apiBase}/rgs</code>.
      </div>
    </Card>
  );
}

function KitField({
  label,
  value,
  copied,
  onCopy,
  secret,
  multiline,
}: {
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  secret?: boolean;
  multiline?: boolean;
}) {
  return (
    <div style={{ display: "grid", gap: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span className="label" style={{ fontSize: 10, color: "var(--fg-3)" }}>
          {label}
        </span>
        <button
          type="button"
          onClick={onCopy}
          style={{
            padding: "3px 10px",
            fontSize: 11,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            background: copied ? "rgba(0,217,126,0.18)" : "rgba(212,162,76,0.12)",
            border: `1px solid ${copied ? "var(--green)" : "var(--gold-bright)"}`,
            color: copied ? "var(--green)" : "var(--gold-bright)",
            borderRadius: 999,
            cursor: "pointer",
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: "10px 12px",
          background: "var(--bg-1)",
          border: "1px solid var(--line)",
          borderRadius: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: secret ? "var(--gold-bright)" : "var(--fg-1)",
          overflow: "auto",
          whiteSpace: multiline ? "pre" : "nowrap",
          maxHeight: multiline ? 160 : undefined,
        }}
      >
        {value}
      </pre>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        padding: "10px 14px",
        textAlign: "left",
        fontFamily: "var(--font-ui)",
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "var(--fg-3)",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return (
    <td
      style={{
        padding: "10px 14px",
        fontFamily: mono ? "var(--font-mono)" : "var(--font-ui)",
        fontSize: 12,
        color: "var(--fg-1)",
      }}
    >
      {children}
    </td>
  );
}

function MiniButton({
  children,
  onClick,
  tone,
  disabled = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  tone: "green" | "gold" | "red" | "cyan";
  disabled?: boolean;
}) {
  const bg = {
    green: "rgba(0,217,126,0.12)",
    gold: "rgba(212,162,76,0.12)",
    red: "rgba(255,59,71,0.12)",
    cyan: "rgba(0,229,255,0.12)",
  }[tone];
  const color = {
    green: "var(--green)",
    gold: "var(--gold-bright)",
    red: "var(--red)",
    cyan: "var(--cyan)",
  }[tone];
  return (
    <button
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{
        padding: "4px 10px",
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        background: bg,
        border: `1px solid ${color}`,
        color,
        borderRadius: 999,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
